/**
 * Test JSON fixtures for all mobile API endpoints.
 * Data structures match the C# server models exactly.
 * Used by the mock adapter for prevalidation testing.
 */

import type {
  NAInterfaceResponseObj,
  InterfaceAuthenticateObj,
  AccountDevicesInfoRespObj,
  DeviceInfoRespObj,
  NATracksRespObj,
  NAGlobalGeofence,
  EventLogRespObj,
  OnDemandRespObj,
  PumpActivityLogRespObj,
  NAVMSSettingsObject,
  NAVoiceCallSettings,
} from '@/api/models';
import { VMSFormsRegion } from '@/types/enums';

// ─── Helper: wrap any response in NAInterfaceResponseObj ───

export function wrapResponse(
  containerObj: unknown,
  authObj?: Partial<InterfaceAuthenticateObj>,
): NAInterfaceResponseObj {
  return {
    messageType: 'RESPONSE',
    messageUUID: crypto.randomUUID(),
    authObj: {
      userID: 'testuser',
      isAuthenticated: true,
      accountUUID: 'acct-uuid-1234-5678-abcd',
      termsAndConditionsAccepted: true,
      cellNumber: '+15551234567',
      ...authObj,
    },
    responseObj: {
      httpStatusCode: 200,
      responseEnum: 0 as never,
    },
    containerObj,
  };
}

// ─── AUTH: accountAuthenticateV2 ───

export const authSuccessResponse = wrapResponse(null, {
  isAuthenticated: true,
  accountUUID: 'acct-uuid-1234-5678-abcd',
  userID: 'testuser',
  termsAndConditionsAccepted: true,
  cellNumber: '+15551234567',
});

export const authFailResponse: NAInterfaceResponseObj = {
  messageType: 'RESPONSE',
  messageUUID: crypto.randomUUID(),
  authObj: {
    userID: '',
    isAuthenticated: false,
    accountUUID: '',
    termsAndConditionsAccepted: false,
  },
  responseObj: {
    httpStatusCode: 401,
    responseEnum: 'ENDPOINT_RESPONSE_UNAUTHORIZED' as never,
  },
  containerObj: null,
};

// ─── DEVICES: accountDevicesV2 ───

export const accountDevicesResponse: AccountDevicesInfoRespObj = {
  deviceList: [
    {
      userInfo: {
        account: {
          accountUUID: 'acct-uuid-1234-5678-abcd',
          userID: 'testuser',
          pushNotificationsEnabled: true,
          cellNumber: '+15551234567',
          emailStr: 'test@nauticalert.com',
          cellVerified: true,
          emailAlertsEnabled: true,
          termsAndConditionsAccepted: true,
          createdDT: '2024-01-15T10:00:00Z',
          updatedDT: '2026-04-27T14:30:00Z',
        },
        isAdmin: true,
        isEmergencyContact: false,
      },
      yachtName: { yachtName: 'Sea Breeze', updatedDT: '2026-03-01T12:00:00Z' },
      deviceProperties: {
        deviceUUID: 'dev-uuid-aaaa-bbbb-cccc',
        imeiStr: '300234010123456',
        deviceType: 'X3' as never,
        deviceTypeStr: 'X3 Pro',
        deviceSerialStr: 'NA-X3P-00142',
        protocolRev: '3.2',
        firmwareRev: '4.1.0',
        buildDT: '2025-06-15T00:00:00Z',
        isDirectConnected: false,
        isIoTCoreConnected: true,
        isDualMode: true,
        networkCarrierStr: 'Hologram',
        modemType: 'QUECTEL_BG96' as never,
        networkCarrier: 'HOLOGRAM' as never,
        communicationType: 'DUAL_MODE' as never,
        onDemandSuspended: false,
        createdDT: '2024-01-10T08:00:00Z',
        updatedDT: '2026-04-27T14:30:00Z',
      },
      gpsLocationData: {
        locationValid: true,
        latitude: 25.7741,
        longitude: -80.1866,
        heading: 145,
        speed: 0.2,
        totalExpectedErrorFt: 15,
        isSignalLost: false,
        updatedDT: '2026-04-27T14:30:00Z',
      },
      extendedProperties: {
        inDegradedGPSSignalState: false,
        vmsAgency: 'NONE' as never,
        statusReportingEnabled: true,
        vesselDocumentationNumber: 'FL4321AB',
      },
    },
    {
      userInfo: {
        account: {
          accountUUID: 'acct-uuid-1234-5678-abcd',
          userID: 'testuser',
          pushNotificationsEnabled: true,
          cellNumber: '+15551234567',
          emailStr: 'test@nauticalert.com',
          cellVerified: true,
          emailAlertsEnabled: true,
          termsAndConditionsAccepted: true,
          createdDT: '2024-01-15T10:00:00Z',
          updatedDT: '2026-04-27T14:30:00Z',
        },
        isAdmin: false,
        isEmergencyContact: true,
      },
      yachtName: { yachtName: 'Ocean Runner', updatedDT: '2026-02-10T09:00:00Z' },
      deviceProperties: {
        deviceUUID: 'dev-uuid-dddd-eeee-ffff',
        imeiStr: '300234010654321',
        deviceType: 'X3' as never,
        deviceTypeStr: 'X3 Mini',
        deviceSerialStr: 'NA-X3M-00087',
        protocolRev: '3.1',
        firmwareRev: '4.0.2',
        isDirectConnected: true,
        isIoTCoreConnected: false,
        isDualMode: false,
        modemType: 'IRIDIUM_9603' as never,
        networkCarrier: 'NONE' as never,
        communicationType: 'SATELLITE' as never,
        onDemandSuspended: false,
        createdDT: '2024-06-20T10:00:00Z',
        updatedDT: '2026-04-26T08:15:00Z',
      },
      gpsLocationData: {
        locationValid: true,
        latitude: 26.1224,
        longitude: -80.1373,
        heading: 270,
        speed: 6.5,
        isSignalLost: false,
        updatedDT: '2026-04-26T08:15:00Z',
      },
      extendedProperties: {
        inDegradedGPSSignalState: false,
        vmsAgency: 'NONE' as never,
        statusReportingEnabled: true,
      },
    },
  ],
};

// ─── DEVICE INFO: deviceInfoV2 (full sensor data) ───

export const deviceInfoResponse: DeviceInfoRespObj = {
  userInfo: accountDevicesResponse.deviceList[0].userInfo,
  contactList: [
    {
      cellStr: '+15551234567',
      isEmergencyContact: false,
      isAdmin: true,
      isXCaliber: true,
      isAssignedByAdmin: false,
    },
    {
      cellStr: '+15559876543',
      isEmergencyContact: true,
      isAdmin: false,
      isXCaliber: false,
      isAssignedByAdmin: true,
    },
    {
      cellStr: '+15555551212',
      isEmergencyContact: false,
      isAdmin: false,
      isXCaliber: false,
      isAssignedByAdmin: true,
    },
  ],
  deviceSubscription: {
    subscriptionType: 'DUAL_MODE' as never,
    subscriptionName: 'Dual Mode Plus',
    dataAllowance: 500,
    globalGeofencingEnabled: true,
    onDemandLimit: 100,
    fleetManagementEnabled: true,
    geosEnabled: false,
    vmsEnabled: true,
    isMigrationNotifyActive: false,
    updatedDT: '2026-01-01T00:00:00Z',
  },
  yachtName: accountDevicesResponse.deviceList[0].yachtName,
  deviceProperties: accountDevicesResponse.deviceList[0].deviceProperties,
  globalSettings: {
    isValid: true,
    alertsEnabled: true,
    sirenEnabled: true,
    vesselTrackingEnabled: true,
    geofenceEnabled: true,
    passwordEnabled: false,
    geofenceUserEnabled: true,
    dataCommEnabled: true,
    subscriptionActive: true,
    sensorDataReportingEnabled: true,
    statusReportingEnabled: true,
    vesselTrackingUpdateDistanceFt: 500,
    vesselTrackingUpdateRateMinutes: 15,
    globalGeofences: 3,
    globalGeofenceUpdateNum: 5,
    dcEnabled: true,
    securityPasscodeEnabled: false,
    securityOperatingMode: 'ONBOARD' as never,
    vmsEnabled: true,
    pumpCycleStatsMinReportingEnabled: true,
    pumpCycleStatsMaxReportingEnabled: false,
    pumpCycleStatsOverrideReportingEnabled: false,
    inNoDischargeZone: false,
    noDischargeAutoDisable: false,
    remoteSirenSyncMode: 'ENABLED' as never,
    messageBeepSetting: 'MESSAGE_BEEP_ONCE' as never,
    firmwareUpdateRequired: false,
    firmwareUpdateAvailable: false,
    coordAutoRescanEnabled: true,
    geoAutoArmSetting: 'GEOFENCE_AUTO_ARM_DISARM_DISABLED' as never,
    geoSirenEnabled: true,
    vmsRegion: 'US' as never,
    vmsOnly: false,
    tenderDataPresent: false,
    tenderGeofenceEnabled: false,
    freezeAlertsEnabled: true,
    preFreezeAlertTemp: 40,
    freezeAlertTemp: 35,
  },
  gpsLocationData: accountDevicesResponse.deviceList[0].gpsLocationData,
  extendedProperties: accountDevicesResponse.deviceList[0].extendedProperties,
  mtObjList: [
    { mtmsn: 1001, messageType: 'GET_SENSOR_DATA_REQUEST', createdDT: '2026-04-27T14:00:00Z', messageReceived: true,  messageAborted: false, messageSent: true  },
    { mtmsn: 1002, messageType: 'GET_DC_TREND_DATA',       createdDT: '2026-04-27T14:05:00Z', messageReceived: true,  messageAborted: false, messageSent: true  },
    { mtmsn: 1003, messageType: 'GET_SECURITY_DATA',       createdDT: '2026-04-27T14:10:00Z', messageReceived: false, messageAborted: false, messageSent: false },
    { mtmsn: 1004, messageType: 'SENSOR_MAP_REQUEST',      createdDT: '2026-04-27T14:15:00Z', messageReceived: false, messageAborted: false, messageSent: false },
  ],
  moObjList: [
    { momsn: 5001, messageType: 'SENSOR_DATA', createdDT: '2026-04-27T14:01:00Z' },
  ],
  smsObjList: [],
  emailObjList: [],
  locationObjList: [],
  pushNotificationObjList: [],
  voiceCallObjList: [],
  sensorMapList: [
    { sourceAddressMSB: 0, sourceAddressLSB: 1, sensorType: 'DC_SENSOR' as never, sensorName: 'House Bank' },
    { sourceAddressMSB: 0, sourceAddressLSB: 2, sensorType: 'DC_SENSOR' as never, sensorName: 'Start Bank' },
    { sourceAddressMSB: 0, sourceAddressLSB: 3, sensorType: 'AC_SENSOR' as never, sensorName: 'Shore Power' },
    { sourceAddressMSB: 0, sourceAddressLSB: 4, sensorType: 'SECURITY_ZONE' as never, sensorName: 'Main Cabin Door' },
    { sourceAddressMSB: 0, sourceAddressLSB: 5, sensorType: 'SECURITY_ZONE' as never, sensorName: 'Fwd Hatch' },
    { sourceAddressMSB: 0, sourceAddressLSB: 6, sensorType: 'NEVATA_SENSOR' as never, sensorName: 'Engine Bilge' },
    { sourceAddressMSB: 0, sourceAddressLSB: 7, sensorType: 'SWITCH_SENSOR' as never, sensorName: 'Float Switch 1' },
    { sourceAddressMSB: 0, sourceAddressLSB: 8, sensorType: 'LOOKOUT_SENSOR_TYPE' as never, sensorName: 'Smoke Detector' },
    { sourceAddressMSB: 0, sourceAddressLSB: 9, sensorType: 'LOOKOUT_SENSOR_TYPE' as never, sensorName: 'Engine Cowling' },
    { sourceAddressMSB: 0, sourceAddressLSB: 10, sensorType: 'LOOKOUT_SENSOR_TYPE' as never, sensorName: 'Salon Motion' },
    { sourceAddressMSB: 0, sourceAddressLSB: 11, sensorType: 'LOOKOUT_SENSOR_TYPE' as never, sensorName: 'Misc Sensor' },
    { sourceAddressMSB: 0, sourceAddressLSB: 12, sensorType: 'SWITCH_SENSOR_TYPE' as never, sensorName: 'Float Switch 2' },
  ],
  sensorData: {
    geofenceList: [
      {
        enabled: true,
        alertPresent: false,
        refLat: 25.7741,
        refLon: -80.1866,
        radius: 1500, // feet
        displacementDataValid: true,
        displacementHeading: 45,
        displacementFeet: 120,
      },
    ],
    // Same records as geofenceListResponse below, echoed here since this is
    // the field the portal actually reads outsideGeofence from (DeviceInfo's
    // copy, not the separate /Geofence/List response).
    globalGeofenceList: [
      {
        geofenceId: 'geo-001',
        refLat: 25.7741,
        refLon: -80.1866,
        radius: 1500, // feet
        name: 'Dinner Key Marina',
        outsideGeofence: false,
        isDeviceSynced: true,
        isInitialized: true,
        createdDT: '2026-03-15T10:00:00Z',
      },
      {
        geofenceId: 'geo-002',
        refLat: 25.0343,
        refLon: -77.3963,
        radius: 3000, // feet
        name: 'Nassau Harbor',
        outsideGeofence: true,
        isDeviceSynced: true,
        isInitialized: true,
        createdDT: '2026-02-20T08:00:00Z',
      },
    ],
    dcSensorList: [
      {
        isEnabled: true,
        onlineState: 'ONLINE' as never,
        sourceAddressMSB: 0,
        sourceAddressLSB: 1,
        sensorName: 'House Bank',
        bankInstance: 1,
        dcVoltage: 12.72,
        dataValid: true,
        alertPresent: false,
        criticalAlertPresent: false,
        dcBankNum: 1,
        descriptor: 'House Battery Bank',
        settingsInitialized: true,
        is24V: false,
        battType: 'BATT_TYPE_AGM' as never,
        normalLevel: 12.4,
        alertLevel: 12.0,
        criticalLevel: 11.5,
        maxVoltage: 14.4,
        minVoltage: 10.5,
        dcFilterValue: 10,
        dcCalibrationOffset: 0,
        updatedDT: '2026-04-27T14:30:00Z',
      },
      {
        isEnabled: true,
        onlineState: 'ONLINE' as never,
        sourceAddressMSB: 0,
        sourceAddressLSB: 2,
        sensorName: 'Start Bank',
        bankInstance: 2,
        dcVoltage: 12.85,
        dataValid: true,
        alertPresent: false,
        criticalAlertPresent: false,
        dcBankNum: 2,
        descriptor: 'Starting Battery Bank',
        settingsInitialized: true,
        is24V: false,
        battType: 'BATT_TYPE_LEAD' as never,
        normalLevel: 12.4,
        alertLevel: 12.0,
        criticalLevel: 11.5,
        maxVoltage: 14.4,
        minVoltage: 10.5,
        dcFilterValue: 10,
        dcCalibrationOffset: 0,
        updatedDT: '2026-04-27T14:30:00Z',
      },
    ],
    dcTrendList: [
      {
        sampleRateMinutes: 15,
        maxVoltage: 14.2,
        minVoltage: 11.8,
        dcVoltageBuff: [
          12.7, 12.65, 12.6, 12.55, 12.5, 12.48, 12.45, 12.42,
          12.4, 12.38, 12.45, 12.6, 12.72, 12.8, 13.2, 13.8,
          14.1, 14.2, 14.15, 14.0, 13.5, 13.0, 12.85, 12.72,
        ],
        bankNum: 1,
        sourceAddressMSB: 0,
        sourceAddressLSB: 1,
        descriptor: 'House Bank',
        updatedDT: '2026-04-27T14:30:00Z',
      },
    ],
    acSensorList: [
      {
        dataValid: true,
        acEnabled: true,
        sensorPresent: true,
        alertPresent: false,
        isEnabled: true,
        sourceAddressMSB: 0,
        sourceAddressLSB: 3,
        onlineState: 'ONLINE' as never,
        sensorName: 'Shore Power',
        acNum: 1,
        pendingUpdate: false,
        descriptor: 'AC Shore Power Input',
        updatedDT: '2026-04-27T14:30:00Z',
      },
    ],
    securitySensorlist: [
      {
        dataValid: true,
        alarmPresent: false,
        isBypassed: false,
        isNotApplicable: false,
        isNC: true,
        alarmType: 'ALARM_TYPE_ZONE' as never,
        armingMode: 'ONBOARD_AND_AWAY' as never,
        sourceAddressMSB: 0,
        sourceAddressLSB: 4,
        zoneInstance: 1,
        onlineState: 'ONLINE' as never,
        sensorName: 'Main Cabin Door',
        descriptor: 'Main Cabin Entry',
        pendingUpdate: false,
        zoneNum: 1,
        deviceCategory: 'DOOR' as never,
        isConnected: true,
        updatedDT: '2026-04-27T14:30:00Z',
      },
      {
        dataValid: true,
        alarmPresent: false,
        isBypassed: true,
        isNotApplicable: false,
        isNC: true,
        alarmType: 'ALARM_TYPE_ZONE' as never,
        armingMode: 'AWAY_ONLY' as never,
        sourceAddressMSB: 0,
        sourceAddressLSB: 5,
        zoneInstance: 2,
        onlineState: 'ONLINE' as never,
        sensorName: 'Fwd Hatch',
        descriptor: 'Forward Deck Hatch',
        pendingUpdate: false,
        zoneNum: 2,
        deviceCategory: 'HATCH' as never,
        isConnected: true,
        updatedDT: '2026-04-27T14:30:00Z',
      },
      {
        dataValid: true,
        alarmPresent: false,
        isBypassed: false,
        isNotApplicable: false,
        isNC: true,
        alarmType: 'ALARM_TYPE_ZONE_SMOKE_V2' as never,
        armingMode: 'ONBOARD_AND_AWAY' as never,
        sourceAddressMSB: 0,
        sourceAddressLSB: 8,
        zoneInstance: 3,
        onlineState: 'ONLINE' as never,
        sensorName: 'Smoke Detector',
        descriptor: 'Smoke Detect',
        pendingUpdate: false,
        zoneNum: 3,
        deviceCategory: 'DEVICE_CATEGORY_SMOKE_CO' as never,
        isConnected: true,
        updatedDT: '2026-04-27T14:30:00Z',
      },
      {
        dataValid: true,
        alarmPresent: false,
        isBypassed: false,
        isNotApplicable: false,
        isNC: true,
        alarmType: 'ALARM_TYPE_ZONE_SECURITY_V2' as never,
        armingMode: 'AWAY' as never,
        sourceAddressMSB: 0,
        sourceAddressLSB: 9,
        zoneInstance: 4,
        onlineState: 'ONLINE' as never,
        sensorName: 'Engine Cowling',
        descriptor: 'Eng Cowling',
        pendingUpdate: false,
        zoneNum: 4,
        deviceCategory: 'DEVICE_CATEGORY_ENGINE_COWLING' as never,
        isConnected: true,
        updatedDT: '2026-04-27T14:30:00Z',
      },
      {
        dataValid: true,
        alarmPresent: false,
        isBypassed: false,
        isNotApplicable: false,
        isNC: true,
        alarmType: 'ALARM_TYPE_MOTION' as never,
        armingMode: 'AWAY' as never,
        sourceAddressMSB: 0,
        sourceAddressLSB: 10,
        zoneInstance: 5,
        onlineState: 'ONLINE' as never,
        sensorName: 'Salon Motion',
        descriptor: 'Salon Motn',
        pendingUpdate: false,
        zoneNum: 5,
        deviceCategory: 'DEVICE_CATEGORY_SECURITY' as never,
        isConnected: true,
        updatedDT: '2026-04-27T14:30:00Z',
      },
      {
        dataValid: true,
        alarmPresent: false,
        isBypassed: false,
        isNotApplicable: false,
        isNC: true,
        alarmType: 'ALARM_TYPE_ZONE_MISC_V2' as never,
        armingMode: 'ONBOARD_AND_AWAY' as never,
        sourceAddressMSB: 0,
        sourceAddressLSB: 11,
        zoneInstance: 6,
        onlineState: 'ONLINE' as never,
        sensorName: 'Misc Sensor',
        descriptor: 'Misc Sensor',
        pendingUpdate: false,
        zoneNum: 6,
        deviceCategory: 'DEVICE_CATEGORY_MISC_NO_SIREN' as never,
        isConnected: true,
        updatedDT: '2026-04-27T14:30:00Z',
      },
    ],
    switchSensorList: [
      {
        onlineState: 'ONLINE' as never,
        sourceAddressMSB: 0,
        sourceAddressLSB: 7,
        sensorName: 'Float Switch 1',
        switchInstance: 1,
        isSwitchOn: false,
        dataValid: true,
        updatedDT: '2026-04-27T14:30:00Z',
      },
      {
        onlineState: 'ONLINE' as never,
        sourceAddressMSB: 0,
        sourceAddressLSB: 12,
        sensorName: 'Float Switch 2',
        switchInstance: 2,
        isSwitchOn: true,
        dataValid: true,
        updatedDT: '2026-04-27T14:30:00Z',
      },
    ],
    nevataSensorList: [
      {
        isValid: true,
        sourceAddressMSB: 0,
        sourceAddressLSB: 6,
        onlineState: 'ONLINE' as never,
        dataValid: true,
        waterLevelValid: true,
        waterLevel: 1.2,
        temperature: 72.5,
        numPumps: 2,
        pumpCounter1: 342,
        pumpCounter2: 158,
        sensorName: 'Engine Bilge',
        isCalibrated: true,
        faultBitMap: 0,
        pumpDischargeSettings: 'PUMPS_ENABLED' as never,
        monitoringOnly: false,
        ignoreSignalLossEnabled: false,
        pumpRuntimeTotal: 1240,
        alertState: 'ALERT_STATE_NORMAL' as never,
        freezeAlertState: 'ALERT_STATE_NORMAL' as never,
        detectNoiseInterference: false,
        numTenthInchesNoiseInterference: 0,
        numSecondsNoiseSettle: 0,
        calibrationOffset: 0,
        numPumpsOverride: 0,
        bilgeLevel: 3,
        sirenLevel: 8,
        criticalLevel: 10,
        pumpActivityMinutes: 60,
        pumpActivityActivations: 5,
        updatedDT: '2026-04-27T14:30:00Z',
      },
    ],
    tenderList: [],
  },
  eventList: [
    {
      eventUUID: 'evt-001',
      updatedDT: '2026-04-27T14:30:00Z',
      createdDT: '2026-04-27T14:30:00Z',
      messageType: 'MESSAGE_TYPE_STATUS' as never,
      flags: 0,
      eventNum: 1001,
      isCriticalAlertState: false,
      isSensorEvent: false,
      messageStr: 'Daily status report received',
      isCurrent: true,
    },
    {
      eventUUID: 'evt-002',
      updatedDT: '2026-04-27T12:15:00Z',
      createdDT: '2026-04-27T12:15:00Z',
      messageType: 'MESSAGE_TYPE_SECURITY' as never,
      flags: 0,
      eventNum: 1000,
      isCriticalAlertState: false,
      isSensorEvent: true,
      messageStr: 'Security mode changed to Onboard',
      isCurrent: true,
    },
    {
      eventUUID: 'evt-003',
      updatedDT: '2026-04-27T08:00:00Z',
      createdDT: '2026-04-27T08:00:00Z',
      messageType: 'MESSAGE_TYPE_DC' as never,
      flags: 0,
      eventNum: 999,
      isCriticalAlertState: false,
      isSensorEvent: true,
      messageStr: 'House Bank voltage normal: 12.72V',
      isCurrent: true,
    },
    {
      eventUUID: 'evt-004',
      updatedDT: '2026-04-26T22:00:00Z',
      createdDT: '2026-04-26T22:00:00Z',
      messageType: 'MESSAGE_TYPE_GEOFENCE' as never,
      flags: 0,
      eventNum: 998,
      isCriticalAlertState: false,
      isSensorEvent: false,
      messageStr: 'Geofence armed - Marina Del Rey',
      isCurrent: true,
    },
    {
      eventUUID: 'evt-005',
      updatedDT: '2026-04-26T18:30:00Z',
      createdDT: '2026-04-26T18:30:00Z',
      messageType: 'MESSAGE_TYPE_ALERT' as never,
      flags: 0,
      eventNum: 997,
      isCriticalAlertState: true,
      cellStr: '+15551234567',
      isSensorEvent: true,
      messageStr: 'AC Shore Power disconnected',
      isCurrent: false,
    },
  ],
};

// ─── TRACKS: locationTrackRequestV2 ───

export const tracksResponse: NATracksRespObj = {
  tracksList: [
    // Track 1: Morning trip from marina and back
    [
      { locationValid: true, latitude: 25.7741, longitude: -80.1866, speed: 0, heading: 0, totalExpectedErrorFt: 25, updatedDT: '2026-04-27T08:00:00Z' },
      { locationValid: true, latitude: 25.7755, longitude: -80.1850, speed: 3.2, heading: 45, totalExpectedErrorFt: 30, updatedDT: '2026-04-27T08:15:00Z' },
      { locationValid: true, latitude: 25.7780, longitude: -80.1820, speed: 5.8, heading: 50, totalExpectedErrorFt: 20, updatedDT: '2026-04-27T08:30:00Z' },
      { locationValid: true, latitude: 25.7810, longitude: -80.1780, speed: 7.1, heading: 55, totalExpectedErrorFt: 15, updatedDT: '2026-04-27T08:45:00Z' },
      { locationValid: true, latitude: 25.7850, longitude: -80.1750, speed: 8.5, heading: 40, totalExpectedErrorFt: 12, updatedDT: '2026-04-27T09:00:00Z' },
      { locationValid: true, latitude: 25.7880, longitude: -80.1700, speed: 6.2, heading: 60, totalExpectedErrorFt: 18, updatedDT: '2026-04-27T09:15:00Z' },
      { locationValid: true, latitude: 25.7900, longitude: -80.1680, speed: 4.0, heading: 90, totalExpectedErrorFt: 22, updatedDT: '2026-04-27T09:30:00Z' },
      { locationValid: true, latitude: 25.7880, longitude: -80.1700, speed: 5.5, heading: 220, totalExpectedErrorFt: 20, updatedDT: '2026-04-27T10:00:00Z' },
      { locationValid: true, latitude: 25.7810, longitude: -80.1780, speed: 7.0, heading: 230, totalExpectedErrorFt: 15, updatedDT: '2026-04-27T10:30:00Z' },
      { locationValid: true, latitude: 25.7741, longitude: -80.1866, speed: 0.2, heading: 145, totalExpectedErrorFt: 25, updatedDT: '2026-04-27T11:00:00Z' },
    ],
    // Track 2: Previous day trip south
    [
      { locationValid: true, latitude: 25.7741, longitude: -80.1866, speed: 0, heading: 0, totalExpectedErrorFt: 20, updatedDT: '2026-04-25T14:00:00Z' },
      { locationValid: true, latitude: 25.7700, longitude: -80.1900, speed: 4.5, heading: 200, totalExpectedErrorFt: 35, updatedDT: '2026-04-25T14:30:00Z' },
      { locationValid: true, latitude: 25.7650, longitude: -80.1950, speed: 6.0, heading: 210, totalExpectedErrorFt: 40, updatedDT: '2026-04-25T15:00:00Z' },
      { locationValid: true, latitude: 25.7600, longitude: -80.2000, speed: 5.0, heading: 220, totalExpectedErrorFt: 50, updatedDT: '2026-04-25T15:30:00Z' },
      { locationValid: true, latitude: 25.7650, longitude: -80.1950, speed: 5.5, heading: 30, totalExpectedErrorFt: 30, updatedDT: '2026-04-25T16:00:00Z' },
      { locationValid: true, latitude: 25.7741, longitude: -80.1866, speed: 0.1, heading: 145, totalExpectedErrorFt: 22, updatedDT: '2026-04-25T16:30:00Z' },
    ],
  ],
};

// ─── GEOFENCE: globalGeofenceListRequestV2 ───

// Bare array -- matches the real globalGeofenceListRequestV2 endpoint, which
// returns respObj.containerObj (a List<NAGlobalGeofence>) directly as the
// response body, not wrapped in a `{ geofenceList: [...] }` object.
export const geofenceListResponse: NAGlobalGeofence[] = [
    {
      geofenceId: 'geo-001',
      refLat: 25.7741,
      refLon: -80.1866,
      radius: 1500, // feet
      name: 'Dinner Key Marina',
      outsideGeofence: false,
      isDeviceSynced: true,
      isInitialized: true,
      createdDT: '2026-03-15T10:00:00Z',
    },
    {
      geofenceId: 'geo-002',
      refLat: 25.0343,
      refLon: -77.3963,
      radius: 3000, // feet
      name: 'Nassau Harbor',
      outsideGeofence: true,
      isDeviceSynced: true,
      isInitialized: true,
      createdDT: '2026-02-20T08:00:00Z',
    },
    {
      geofenceId: 'geo-003',
      refLat: 24.5551,
      refLon: -81.7800,
      radius: 2000, // feet
      name: 'Key West Bight',
      outsideGeofence: true,
      isDeviceSynced: false,
      isInitialized: true,
      createdDT: '2026-01-10T14:00:00Z',
    },
];

// ─── EVENTS: eventLogV2 ───

export const eventLogResponse: EventLogRespObj = {
  eventList: deviceInfoResponse.eventList,
};

// ─── ON-DEMAND: onDemandRequestV2 ───

export const onDemandSuccessResponse: OnDemandRespObj = {
  isSuccess: true,
  mtmsn: 1003,
};

// ─── PUMP: pumpActivityLogV2 ───

export const pumpActivityLogResponse: PumpActivityLogRespObj = {
  entries: [
    { timestamp: '2026-04-27T14:00:00Z', pumpNumber: 1, runtime: 45, activationLevel: 3, current: 2.1, temperature: 72.5 },
    { timestamp: '2026-04-27T10:00:00Z', pumpNumber: 1, runtime: 30, activationLevel: 4, current: 2.3, temperature: 71.0 },
    { timestamp: '2026-04-26T22:00:00Z', pumpNumber: 2, runtime: 15, activationLevel: 2, current: 1.8, temperature: 70.0 },
    { timestamp: '2026-04-26T16:00:00Z', pumpNumber: 1, runtime: 60, activationLevel: 5, current: 2.5, temperature: 73.5 },
    { timestamp: '2026-04-26T08:00:00Z', pumpNumber: 1, runtime: 20, activationLevel: 3, current: 2.0, temperature: 68.0 },
  ],
};

// ─── VMS SETTINGS: vmsSettingsV2 ───

export const vmsSettingsResponse: NAVMSSettingsObject = {
  isValid: true,
  vmsRegionEnum: VMSFormsRegion.US,
  vesselIDStr: 'FL4321AB',
  vesselNameStr: 'Sea Breeze',
  captainFirstNameStr: 'John',
  captainLastNameStr: 'Smith',
  updatedDT: '2026-03-01T12:00:00Z',
};

// ─── VOICE CALL SETTINGS: voiceCallSettingsV2 ───

export const voiceCallSettingsResponse: NAVoiceCallSettings = {
  geofenceEventsEnabled: true,
  intrusionEventsEnabled: true,
  smokeEventsEnabled: true,
  coEventsEnabled: true,
  highWaterEventsEnabled: true,
  miscZoneTypeEventsEnabled: false,
};

// ─── TERMS: nauticAlertTermsAndConditionsV2 ───

export const termsAndConditionsText = `
<h1>Nautic Alert Terms and Conditions</h1>
<p>Last updated: January 1, 2026</p>
<h2>1. Acceptance of Terms</h2>
<p>By accessing and using the Nautic Alert vessel monitoring service, you agree to these terms and conditions.</p>
<h2>2. Service Description</h2>
<p>Nautic Alert provides satellite and cellular-based vessel monitoring, including GPS tracking, sensor monitoring, security alerts, and remote device management.</p>
<h2>3. User Responsibilities</h2>
<p>Users are responsible for maintaining accurate account information and ensuring authorized access to their devices.</p>
<h2>4. Privacy</h2>
<p>Location and sensor data are collected for monitoring purposes. See our Privacy Policy for details.</p>
`;
